import React, { MouseEventHandler, useContext } from 'react'
import { isDownloadOk } from 'src/Utils/download'
import { useLocation, useNavigate } from 'react-router-dom'
import log from 'loglevel'
import FileSaver from 'file-saver'
import { ToastPrompts, showInfoToast } from 'src/Utils/helpers/toast'
import { editDocumentMarkdown } from 'src/pages/Policies/Policies.helpers'
import { PolicyPromiseClient } from '@trustero/trustero-api-web/lib/model/policy_grpc_web_pb'
import { GetPolicyDocumentRequest } from '@trustero/trustero-api-web/lib/model/policy_pb'
import { ContentStorePromiseClient } from '@trustero/trustero-api-web/lib/contentstore/contentstore_grpc_web_pb'
import { ContentID } from '@trustero/trustero-api-web/lib/contentstore/contentstore_pb'
import { StringValue } from 'google-protobuf/google/protobuf/wrappers_pb'
import { HTTP_METHODS } from 'src/Utils/globalEnums'
import { useInAudit } from 'src/context/AuditContext'
import { useAuthorizedGrpcClient } from '../../../../../adapter'
import { Spinner, ThrobberContext } from '../../../../../Throbber'

export function useDocumentOnClick({
  documentId,
  setMarkdownBody,
}: {
  documentId: string
  setMarkdownBody: React.Dispatch<React.SetStateAction<string>>
}): MouseEventHandler<HTMLButtonElement> | undefined {
  const { setThrobberState } = useContext(ThrobberContext)
  const { auditId } = useInAudit()
  const policyClient = useAuthorizedGrpcClient(PolicyPromiseClient)
  const contentClient = useAuthorizedGrpcClient(ContentStorePromiseClient)

  const navigate = useNavigate()
  const location = useLocation()

  const downloadDocumentBody = (documentId: string) => {
    return async (e: React.MouseEvent) => {
      e.preventDefault()
      e.stopPropagation()

      setThrobberState({
        isShown: true,
        Display: <Spinner color="primary" size="xl" />,
      })

      try {
        const record = await policyClient.getPolicyDocument(
          new GetPolicyDocumentRequest().setId(documentId),
        )
        const contentId = record.getContentId()
        const mime = record.getMime()
        if (!contentId) {
          showInfoToast(ToastPrompts.DOC_DOWNLOAD_ERROR)
          return
        }
        const contentRequest = new ContentID().setContentId(contentId)
        if (auditId) {
          contentRequest.setAuditId(new StringValue().setValue(auditId))
        }
        const url = await contentClient.getDownloadURL(contentRequest)
        const response = await fetch(url.getUrl(), {
          method: HTTP_METHODS.GET,
          headers: new Headers(
            url.getHeadersMap().getEntryList() as unknown as HeadersInit,
          ),
        })
        if (!isDownloadOk(response)) {
          showInfoToast(ToastPrompts.DOC_DOWNLOAD_ERROR)
          return
        }

        if (mime === 'text/markdown') {
          const material = await response.text()
          setMarkdownBody(material)
          editDocumentMarkdown(
            documentId,
            material,
            setMarkdownBody,
            navigate,
            location,
          )
        } else {
          const bodyBlob = await response.blob()
          FileSaver.saveAs(
            new Blob([bodyBlob], { type: mime }),
            record.getCaption() || documentId,
          )
        }
      } catch (err) {
        log.error('Error in useDocumentOnClick', err)
        showInfoToast(ToastPrompts.DOC_DOWNLOAD_ERROR)
      } finally {
        setThrobberState({
          isShown: false,
          Display: <></>,
        })
      }
    }
  }

  return downloadDocumentBody(documentId)
}
